import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, Bed } from "@phosphor-icons/react";
import AttendanceHeatmap from "../components/AttendanceHeatmap";
import HolidayList from "../components/HolidayList";

const STATUS_COLORS = {
  Present: "#10B981",
  Absent: "#DC2626",
  "Half-Day": "#FBBF24",
  Leave: "#6B7280",
};

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [today] = useState(() => new Date().toISOString().slice(0, 10));
  const [selected, setSelected] = useState(today);
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statuses, setStatuses] = useState(["Present", "Absent", "Half-Day", "Leave"]);
  const [heatVersion, setHeatVersion] = useState(0); // bumped after marking → forces heatmap refetch

  const load = async () => {
    const [{ data: recs }, settings] = await Promise.all([
      api.get("/attendance", { params: isAdmin ? { date_from: selected, date_to: selected } : {} }),
      api.get("/settings"),
    ]);
    setRecords(recs);
    if (settings.data?.attendance_status?.length) {
      setStatuses(settings.data.attendance_status.map((s) => s.value));
    }
    if (isAdmin) {
      const { data: emps } = await api.get("/employees");
      setEmployees(emps);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [selected]);

  const markSelf = async (status) => {
    try {
      await api.post("/attendance", { date: today, status });
      toast.success(`Marked ${status} for ${today}`);
      setHeatVersion((v) => v + 1);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markFor = async (empId, status) => {
    try {
      await api.post("/attendance", { date: selected, status, employee_id: empId });
      toast.success("Attendance updated");
      setHeatVersion((v) => v + 1);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const statusOf = (empId) => records.find((r) => r.employee_id === empId)?.status;

  return (
    <div>
      <PageHeader
        eyebrow="Daily Register"
        title="Attendance"
        subtitle={isAdmin ? "Mark or edit attendance for the entire team." : "Mark your daily attendance in one click."}
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <AttendanceHeatmap refreshKey={heatVersion} />

        <HolidayList />

        {!isAdmin && (
          <div className="bg-white border border-[#E5E7EB] p-6">
            <div className="label-caps mb-2">Mark today · {today}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {statuses.map((s) => {
                const active = statusOf(user.id) === s;
                return (
                  <button key={s} onClick={() => markSelf(s)} data-testid={`mark-self-${s.toLowerCase()}`}
                          className={`p-4 border text-sm font-semibold transition-colors duration-200 ${active ? "border-[#106109] bg-[#F4F6FC] text-[#106109]" : "border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}>
                    <StatusIcon s={s} />
                    <div className="mt-2">{s}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isAdmin && (
          <>
            <div className="flex items-center gap-3">
              <label className="label-caps">Date</label>
              <input type="date" value={selected} onChange={(e) => setSelected(e.target.value)}
                     className="border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="attendance-date" />
            </div>
            <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr className="text-left">
                    {["Emp ID", "Name", "Department", "Status", "Mark"].map((h) => (
                      <th key={h} className="px-4 py-3 label-caps">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((e) => {
                    const st = statusOf(e.id);
                    return (
                      <tr key={e.id} className="border-b border-[#F3F4F6]" data-testid={`att-row-${e.employee_id}`}>
                        <td className="px-4 py-3 font-mono font-semibold">{e.employee_id}</td>
                        <td className="px-4 py-3">{e.name}</td>
                        <td className="px-4 py-3">{e.department}</td>
                        <td className="px-4 py-3">
                          {st ? (
                            <span className="inline-flex items-center gap-2 px-2 py-1 text-xs font-semibold" style={{ background: `${STATUS_COLORS[st] || "#6B7280"}20`, color: STATUS_COLORS[st] || "#6B7280" }}>{st}</span>
                          ) : <span className="text-xs text-[#9CA3AF]">Not marked</span>}
                        </td>
                        <td className="px-4 py-3">
                          <select value={st || ""} onChange={(ev) => markFor(e.id, ev.target.value)}
                                  className="border border-[#E5E7EB] px-2 py-1 text-sm" data-testid={`att-select-${e.employee_id}`}>
                            <option value="" disabled>Choose…</option>
                            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!isAdmin && (
          <div className="bg-white border border-[#E5E7EB]">
            <div className="px-6 py-4 border-b border-[#E5E7EB]">
              <div className="label-caps">History</div>
              <div className="font-display font-bold text-xl mt-1">My attendance log</div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr><th className="px-4 py-3 label-caps text-left">Date</th><th className="px-4 py-3 label-caps text-left">Status</th></tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className="border-b border-[#F3F4F6]">
                    <td className="px-4 py-3">{r.date}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 px-2 py-1 text-xs font-semibold" style={{ background: `${STATUS_COLORS[r.status] || "#6B7280"}20`, color: STATUS_COLORS[r.status] || "#6B7280" }}>{r.status}</span>
                    </td>
                  </tr>
                ))}
                {records.length === 0 && <tr><td colSpan={2} className="px-4 py-10 text-center text-[#9CA3AF]">No attendance marked yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusIcon({ s }) {
  const size = 22;
  if (s === "Present") return <CheckCircle size={size} weight="fill" color="#10B981" />;
  if (s === "Absent") return <XCircle size={size} weight="fill" color="#DC2626" />;
  if (s === "Half-Day") return <Clock size={size} weight="fill" color="#FBBF24" />;
  return <Bed size={size} weight="fill" color="#6B7280" />;
}
